import { UIChart } from '../chart/ui-chart';
import { UIIcon } from '../../../shared-components/components/icon/ui-icon';
import { Component } from '../../../shared-components/decorators/component.decorator';
import type { Point, Series, Chart } from 'highcharts';
import {
    ExtendedPlotSeriesOptions,
    ExtendedPoint,
    ExtendedDataLabelsOptions,
    UIChartSeriesOption,
} from '../charts.types';
import styles from './ui-chart-pie.css';

/** @internal */
interface PieDataLabelContext {
    percentage: number;
    point: Point & { index: number };
    y: number | null;
    series: Series;
}

/**
 * @class
 * @memberof ChartsComponents
 * @element ui-chart-pie
 * @augments {UIChart}
 * @alias UIChartPie
 * @classdesc Represents a class for <code>ui-chart-pie</code> element.
 * Draws pie chart.
 * @slot {@type "ui-series"}
 * @example
 * <ui-chart-pie units="%">
 *   <ui-series name="Swedbank cards">
 *     <ui-point y="53" name="Swedbank cards"></ui-point>
 *     <ui-point y="20" name="SEB cards"></ui-point>
 *     <ui-point y="27" name="Other cards"></ui-point>
 *   </ui-series>
 * </ui-chart-pie>
 */
@Component({ tag: 'ui-chart-pie', styles })
export class UIChartPie extends UIChart {
    static readonly HIDE_LABEL_PERCENT_THRESHOLD = 5;

    override get type(): string {
        return 'pie';
    }

    /**
     * Custom label formatter for data labels with icons.
     * @param {UIChart} current
     * @param {percents} [percents]
     * @returns {string}
     */
    static customDataLabelFormatter(
        this: PieDataLabelContext,
        current: UIChart,
        percents?: boolean
    ): string {
        let retval = '';
        current
            .getOptions()
            .series?.forEach((seriesOpt: UIChartSeriesOption) => {
                const series = seriesOpt;
                if (series.data?.[this.point.index]?.icon) {
                    current.setAttribute('with-icons', 'true');
                    if (
                        this.percentage >=
                            UIChartPie.HIDE_LABEL_PERCENT_THRESHOLD ||
                        (this.percentage === 0 && series.data.length === 1)
                    ) {
                        const text = percents
                            ? Math.round(
                                  current.calculatePointPercentage(
                                      this.series,
                                      this.y ?? 0
                                  )
                              ) + '%'
                            : (this.y ?? 0) + current.units;
                        const customLabel = current.createElement({
                            tagName: 'div',
                            classList: {
                                'ui-chart-pie__data-label': true,
                            },
                            children: [
                                {
                                    tagName: 'ui-icon',
                                    attributes: {
                                        glyph: series.data[this.point.index]
                                            .icon!,
                                        color: UIIcon.colors.WHITE,
                                    },
                                },
                                {
                                    tagName: 'span',
                                    children: [text],
                                },
                            ],
                        });
                        retval += customLabel.outerHTML;
                    }
                } else {
                    retval += current.createElement({
                        tagName: 'span',
                        classList: {
                            'piechart-custom-datalabel': true,
                            'ui-chart-pie__data-label': true,
                        },
                        children: [
                            {
                                tagName: 'span',
                                classList: {
                                    'piechart-custom-datalabel-name': true,
                                },
                                children: [this.point.name ?? ''],
                            },
                            {
                                tagName: 'span',
                                classList: {
                                    'piechart-custom-datalabel-value': true,
                                },
                                children: [String(this.point.y), current.units],
                            },
                        ],
                    }).outerHTML;
                }
            });
        return retval;
    }

    /**
     * Sets correctly centered position into sector for custom labels in pie chart.
     */
    static customLabelPlacer(this: Chart): void {
        this.series.forEach((series: Series) => {
            series.data.forEach((point: ExtendedPoint) => {
                const dataLabel = point.dataLabel;
                if (dataLabel?.div) {
                    const bbox = dataLabel.div.querySelector<HTMLElement>(
                        '.ui-chart-pie__data-label'
                    );
                    const iconWidthOffset = bbox
                        ? bbox.getBoundingClientRect().width / 2
                        : 0;
                    const chart = point.series.chart;
                    const cx = chart.series[0].center?.[0] ?? 0;
                    const cy = chart.series[0].center?.[1] ?? 0;
                    const shapeArgs = point.shapeArgs as
                        | { r: number; innerR: number }
                        | undefined;
                    const mr = shapeArgs
                        ? (shapeArgs.r + shapeArgs.innerR) / 2
                        : 0;
                    const angle = point.angle;
                    const { x, y } = UIChart.calculateCircleCoords(
                        mr,
                        angle,
                        0,
                        0
                    );
                    const dx = cx + x;
                    const dy = cy + y;
                    dataLabel.attr({
                        x: dx - iconWidthOffset,
                        y: dy,
                    });
                }
            });
        });
    }

    /**
     * @inheritDoc
     */
    override renderChart(): void {
        this.mergeOptionsFromElement();
        const currentChart = this.instance;
        const pieDataLabels: ExtendedDataLabelsOptions = {
            enabled: true,
            useHTML: true,
            align: 'center',
            overflow: 'justify',
            distance: 0,
            formatter: function (this: PieDataLabelContext): string {
                return UIChartPie.customDataLabelFormatter.call(
                    this,
                    currentChart
                );
            },
        };
        const seriesOptions: ExtendedPlotSeriesOptions = {
            showInLegend: true,
            dataLabels: pieDataLabels,
        };
        this.draw({
            chart: {
                events: {
                    render: function (this: Chart): void {
                        UIChartPie.customLabelPlacer.call(this);
                    },
                },
            },
            plotOptions: {
                series: seriesOptions,
            },
            legend: {
                x: -10, // -10 due to default point is hidden
            },
        });
    }
}
